import { capitalize } from "@mui/material";
import { defaultNewFilters } from "components/AnalyticsFilter";
import { normalizeDateByPeriod } from "components/LineChart";
import { useState } from "react";
import { dateToUsFormat } from "utils/helpers";

export const useAnalitycs = () => {
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState(defaultNewFilters);
    const [data, setData] = useState([]);
    const [totalData, setTotalData] = useState(null);
    const [subtotalData, setSubtotalData] = useState([]);
    const [subtotalNqrData, setSubtotalNqrData] = useState([]);
    const [itemName, setItemName] = useState("Loading...");
    const [period, setPeriod] = useState("Daily");
  
    const newLine = "\r\n";
  
    const addCsvHeaderLine = (csvContent, header) => {
      return csvContent + newLine + `"${header}"` + newLine;
    };
  
    const createCsvLineFromArray = (arr) => {
      return (arr ?? []).map((value) => `"${value ?? "-"}"`).join(",");
    };
  
    const createCsvListFromObject = (obj) => {
      const csvContent = [
        ...Object.entries(obj).map(
          ([key, value]) => `"${capitalize(key)}","${value ?? "-"}"`
        ),
      ].join(newLine);
  
      return csvContent + newLine;
    };
  
    const convertCamelCaseToTitle = (str) => {
      return str
        .replace(/([A-Z])/g, " $1") // Add a space before each uppercase letter
        .replace(/^./, (firstChar) => firstChar.toUpperCase()); // Capitalize the first letter
    };
  
    const addCsvTableToContentFromObj = (csvContent, obj) => {
      return csvContent + createCsvListFromObject(obj) + newLine;
    };
  
    const addLineToContentFromArray = (csvContent, arr) => {
      return csvContent + createCsvLineFromArray(arr) + newLine;
    };
  
    const getFormattedDateTime = () => {
      const now = new Date();
      const year = now.getFullYear();
      const month = (now.getMonth() + 1).toString().padStart(2, "0");
      const day = now.getDate().toString().padStart(2, "0");
      const hours = now.getHours().toString().padStart(2, "0");
      const minutes = now.getMinutes().toString().padStart(2, "0");
      const seconds = now.getSeconds().toString().padStart(2, "0");
      return `${year}-${month}-${day}_${hours}-${minutes}-${seconds}`;
    };
  
    const getSqlDateTime = () => {
      const now = new Date();
      const year = now.getFullYear();
      const month = (now.getMonth() + 1).toString().padStart(2, "0");
      const day = now.getDate().toString().padStart(2, "0");
      const hours = now.getHours().toString().padStart(2, "0");
      const minutes = now.getMinutes().toString().padStart(2, "0");
      const seconds = now.getSeconds().toString().padStart(2, "0");
      return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
    };
  
    const handleDownload = () => {
      const formattedDateTime = getFormattedDateTime();
  
      let csvContent =
        `"Analytics Report from:", "${dateToUsFormat(getSqlDateTime())}"` +
        newLine;
      csvContent = addCsvHeaderLine(csvContent, "Filter Parameters:");
      csvContent = addCsvTableToContentFromObj(csvContent, filter);
      csvContent += newLine;
      const totalNames = Object.keys(totalData);
      csvContent = addLineToContentFromArray(
        csvContent,
        totalNames.map((name, index) =>
          index ? convertCamelCaseToTitle(capitalize(name)) : itemName
        )
      );
      csvContent = addLineToContentFromArray(
        csvContent,
        totalNames.map((name) => totalData[name])
      );
  
      csvContent += newLine;
      csvContent += newLine;
      const subtotalNqrDataNames = [
        itemName,
        "Notifications",
        "Questions",
        "Referrals",
        "Clicks To Pay",
        "Canceled",
        "Total Clicks",
        "Clicks From",
        "Clicks To",
        "Total Buttons",
      ];
      csvContent = addLineToContentFromArray(csvContent, subtotalNqrDataNames);
      [...(subtotalNqrData ?? [])]
        .sort((a, b) => (a?.[1] < b?.[1] ? -1 : 1))
        .forEach((row) => {
          if (!row?.length) return [];
          const newRow = [
            row[1],
            row[2],
            row[3],
            row[4],
            row[5],
            row[6],
            row[7],
            row[8],
            row[9],
            row[12],
          ];
          csvContent = addLineToContentFromArray(csvContent, newRow);
        });
  
      csvContent += newLine;
      csvContent += newLine;
      const subtotalDataNames = [
        itemName,
        "Digital Clicks",
        "Physical Clicks",
        "Total Clicks",
        "Clicks From",
        "Clicks To",
        "Digital Buttons",
        "Physical Buttons",
        "Total Buttons",
      ];
      csvContent = addLineToContentFromArray(csvContent, subtotalDataNames);
      subtotalData.forEach((row) => {
        const newRow = [
          row[2],
          row[3],
          row[4],
          row[5],
          row[6],
          row[7],
          row[8],
          row[9],
          row[10],
        ];
        csvContent = addLineToContentFromArray(csvContent, newRow);
      });
  
      csvContent += newLine;
      csvContent += newLine;
      const dataNames = [
        "Created At",
        "Total Clicks",
        "Soft Clicks",
        "Hard Clicks",
        "Notifications",
        "Questions",
        "Referrals",
        "Canceled",
        "Clicks To Pay",
        "Buttons Count",
        "Total Calcelled",
        "Total To Pay",
        "Total Clicks",
        "Total Buttons",
      ];
      csvContent = addLineToContentFromArray(csvContent, dataNames);
      data.forEach((row) => {
        const newRow = [
          normalizeDateByPeriod(row[0], period),
          row[1],
          row[2],
          row[3],
          row[4],
          row[5],
          row[6],
          row[7],
          row[8],
          row[9],
          row[10],
          row[11],
          row[12],
          row[13],
        ];
        csvContent = addLineToContentFromArray(csvContent, newRow);
      });
  
      const downloadUrl = window.URL.createObjectURL(new Blob([csvContent]));
      const link = document.createElement("a");
      link.href = downloadUrl;
      link.setAttribute("download", `analytics_${formattedDateTime}.csv`);
      document.body.appendChild(link);
      link.click();
    };

  return (
    {
      loading,
      setLoading,
      setFilter,
      data,
      setData,
      totalData,
      setTotalData,
      subtotalData,
      setSubtotalData,
      subtotalNqrData,
      setSubtotalNqrData,
      itemName,
      setItemName,
      period,
      setPeriod,
      handleDownload,
    }
  );
};
